import React, { useEffect, useState } from 'react';
import {
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ServerIcon,
  CurrencyDollarIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { adminService } from '../services/authService';

interface DashboardStats {
  totalUsers: number;
  totalDreams: number;
  activeUsers: number;
  recentUsers: number;
  recentDreams: number;
  totalInterpretations: number;
  totalImages: number;
  costs: {
    ai: number;
    runware: number;
    astrology: number;
    total: number;
    tokens: number;
    runwareRequests?: number;
    astrologyRequests?: number;
  };
  serverStatus: 'online' | 'offline';
  lastUpdated: string;
}

interface ApiHealthStatus {
  name: string;
  status: string;
  latencyMs?: number | null;
  message?: string;
}

interface CostDetails {
  period: {
    days: number;
    tokens: number;
    costs: {
      ai: number;
      runware: number;
      astrology: number;
      total: number;
    };
    modelBreakdown: Array<{
      model: string;
      _sum: {
        totalTokens: number | null;
        totalCost: number | null;
      };
    }>;
  };
  total: {
    tokens: number;
    costs: {
      ai: number;
      runware: number;
      astrology: number;
      total: number;
    };
  };
  rates: {
    runwareCostPerImage: number;
    astrologyCostPerRequest: number;
  };
}

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalDreams: 0,
    activeUsers: 0,
    recentUsers: 0,
    recentDreams: 0,
    totalInterpretations: 0,
    totalImages: 0,
    costs: {
      ai: 0,
      runware: 0,
      astrology: 0,
      total: 0,
      tokens: 0,
      runwareRequests: 0,
      astrologyRequests: 0,
    },
    serverStatus: 'offline',
    lastUpdated: '',
  });
  const [loading, setLoading] = useState(true);
  const [apiHealth, setApiHealth] = useState<ApiHealthStatus[]>([]);
  const [costDetails, setCostDetails] = useState<CostDetails | null>(null);
  const [azureCredit, setAzureCredit] = useState<Awaited<
    ReturnType<typeof adminService.getAzureCredit>
  > | null>(null);

  useEffect(() => {
    fetchDashboardStats();
    fetchApiHealth();
    fetchCostDetails();
    fetchAzureCredit();
  }, []);

  const fetchAzureCredit = async () => {
    try {
      const data = await adminService.getAzureCredit();
      setAzureCredit(data);
    } catch (error) {
      console.error('Error fetching Azure credit:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const data = await adminService.getStats();
      setStats(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setLoading(false);
    }
  };

  const fetchApiHealth = async () => {
    try {
      const data = await adminService.getApiHealth();
      setApiHealth(data.services || []);
    } catch (error) {
      console.error('Error fetching API health:', error);
    }
  };

  const fetchCostDetails = async () => {
    try {
      const data = await adminService.getCosts();
      setCostDetails(data);
    } catch (error) {
      console.error('Error fetching cost details:', error);
    }
  };

  const statCards = [
    {
      name: 'Toplam Kullanıcı',
      value: stats.totalUsers.toLocaleString(),
      icon: UsersIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Toplam Rüya',
      value: stats.totalDreams.toLocaleString(),
      icon: DocumentTextIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Aktif Kullanıcı',
      value: stats.activeUsers.toLocaleString(),
      icon: ChartBarIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Toplam Maliyet',
      value: `$${(stats.costs.total || 0).toFixed(2)}`,
      icon: CurrencyDollarIcon,
      color: 'bg-purple-500',
    },
    {
      name: 'Token Kullanımı',
      value: (stats.costs.tokens || 0).toLocaleString(),
      icon: CpuChipIcon,
      color: 'bg-indigo-500',
    },
    {
      name: 'Sunucu Durumu',
      value: stats.serverStatus === 'online' ? 'Çevrimiçi' : 'Çevrimdışı',
      icon: ServerIcon,
      color: stats.serverStatus === 'online' ? 'bg-green-500' : 'bg-red-500',
    },
  ];

  const getStatusBadge = (status: string) => {
    if (status === 'online') {
      return 'bg-green-100 text-green-800';
    }
    if (status === 'missing-key') {
      return 'bg-yellow-100 text-yellow-800';
    }
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Ruyan uygulamasının genel durumu ve istatistikleri
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 mb-8">
        {statCards.map((card) => (
          <div
            key={card.name}
            className="bg-white overflow-hidden shadow rounded-lg"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`p-3 rounded-md ${card.color}`}>
                    <card.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {card.name}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {card.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Azure Credit Tracker (full-width) */}
      {azureCredit && (
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Azure Credit Tracker
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Blog (ruyan.app) içerik üretimi · Manuel güncelleme:
                  {' '}env <code className="bg-gray-100 px-1 rounded">AZURE_CREDIT_BALANCE</code>
                </p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  azureCredit.status === 'surplus'
                    ? 'bg-green-100 text-green-800'
                    : azureCredit.status === 'tight'
                    ? 'bg-yellow-100 text-yellow-800'
                    : azureCredit.status === 'depleting'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {azureCredit.status === 'surplus'
                  ? '✓ Atıl risk'
                  : azureCredit.status === 'tight'
                  ? '⚠ Sıkışık'
                  : azureCredit.status === 'depleting'
                  ? '🚨 Bitiyor'
                  : '? Bilinmiyor'}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Bakiye</p>
                <p className="text-2xl font-light text-gray-900">
                  ${azureCredit.balance.toFixed(0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Son Geçerlilik</p>
                <p className="text-sm font-medium text-gray-700 mt-1">
                  {azureCredit.expiry
                    ? new Date(azureCredit.expiry).toLocaleDateString('tr-TR', {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })
                    : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {azureCredit.daysUntilExpiry !== null
                    ? `${azureCredit.daysUntilExpiry} gün kaldı`
                    : ''}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Günlük Yakım</p>
                <p className="text-2xl font-light text-gray-900">
                  ${azureCredit.dailyBurn.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">tahmini</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Tahmini Harcama</p>
                <p className="text-2xl font-light text-gray-900">
                  ${(azureCredit.projectedSpend ?? 0).toFixed(0)}
                </p>
                <p className="text-xs text-gray-500">son geçerliliğe kadar</p>
              </div>
            </div>

            {/* Utilization bar */}
            {azureCredit.utilization !== null && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Kullanım projeksiyonu</span>
                  <span>%{azureCredit.utilization.toFixed(1)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      azureCredit.status === 'depleting'
                        ? 'bg-red-500'
                        : azureCredit.status === 'tight'
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, azureCredit.utilization)}%` }}
                  />
                </div>
              </div>
            )}

            {azureCredit.recommendation && (
              <div
                className={`mt-3 p-3 rounded-md text-sm ${
                  azureCredit.status === 'depleting'
                    ? 'bg-red-50 text-red-900 border border-red-200'
                    : azureCredit.status === 'tight'
                    ? 'bg-yellow-50 text-yellow-900 border border-yellow-200'
                    : 'bg-blue-50 text-blue-900 border border-blue-200'
                }`}
              >
                <span className="font-medium">Öneri:</span> {azureCredit.recommendation}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cost & KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Maliyet Dağılımı
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">AI (OpenAI)</span>
                <span className="text-sm font-medium">
                  ${(stats.costs.ai || 0).toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Runware (Görsel)</span>
                <span className="text-sm font-medium">
                  ${(stats.costs.runware || 0).toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">FreeAstrology</span>
                <span className="text-sm font-medium">
                  ${(stats.costs.astrology || 0).toFixed(4)}
                </span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-900">
                    Toplam
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    ${(stats.costs.total || 0).toFixed(4)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Son 7 Gün
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Yeni Kullanıcı</span>
                <span className="text-sm font-medium">{stats.recentUsers}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Yeni Rüya</span>
                <span className="text-sm font-medium">{stats.recentDreams}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Toplam Yorum</span>
                <span className="text-sm font-medium">
                  {stats.totalInterpretations}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Görsel Oluşturma</span>
                <span className="text-sm font-medium">{stats.totalImages}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System & API Health */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Sistem Durumu
            </h3>
            <div className="text-sm text-gray-500 space-y-2">
              <p>
                • Sunucu:{' '}
                {stats.serverStatus === 'online' ? 'Çevrimiçi' : 'Çevrimdışı'}
              </p>
              <p>
                • Son güncelleme:{' '}
                {stats.lastUpdated
                  ? new Date(stats.lastUpdated).toLocaleString('tr-TR')
                  : '-'}
              </p>
              <p>• Token kullanımı: {stats.costs.tokens.toLocaleString()}</p>
              <p>
                • Ortalama maliyet/rüya:{' '}
                {stats.totalDreams > 0
                  ? `$${(stats.costs.total / stats.totalDreams).toFixed(4)}`
                  : '—'}
              </p>
              <p>
                • Runware istekleri:{' '}
                {stats.costs.runwareRequests?.toLocaleString() || 0}
              </p>
              <p>
                • Astroloji istekleri:{' '}
                {stats.costs.astrologyRequests?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              API Sağlığı
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {apiHealth.length === 0 && (
                <p className="text-sm text-gray-500">
                  API sağlık bilgisi yüklenemedi.
                </p>
              )}
              {apiHealth.map((service) => (
                <div
                  key={service.name}
                  className="rounded-lg border border-gray-200 p-4"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">
                      {service.name}
                    </p>
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(
                        service.status,
                      )}`}
                    >
                      {service.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Gecikme:{' '}
                    {service.latencyMs || service.latencyMs === 0
                      ? `${service.latencyMs} ms`
                      : '—'}
                  </p>
                  {service.message && (
                    <p className="mt-1 text-xs text-red-500">
                      {service.message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Model Breakdown */}
      {costDetails?.period?.modelBreakdown?.length ? (
        <div className="bg-white shadow rounded-lg mt-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Model Bazında Maliyet (Son {costDetails.period.days} gün)
                </h3>
                <p className="text-sm text-gray-500">
                  Toplam token: {costDetails.period.tokens.toLocaleString()}
                </p>
              </div>
              <div className="text-sm text-gray-500">
                <p>Runware: ${costDetails.period.costs.runware.toFixed(4)}</p>
                <p>
                  Astroloji: ${costDetails.period.costs.astrology.toFixed(4)}
                </p>
              </div>
            </div>
            <div className="overflow-x-auto mt-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Model
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Token
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Maliyet
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {costDetails.period.modelBreakdown.map((item) => (
                    <tr key={item.model}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {item.model}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {(item._sum.totalTokens || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        ${(item._sum.totalCost || 0).toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DashboardPage;
